import {
    OfferRequestBuildingType,
    pickLocalizedText,
    settableOfferRequestReportStatusSchema,
    type LocalizedText,
    type OfferRequestTimeConstraint,
} from '@houzy/schemas/offer-request-management';
import type { PartnerCockpitLeadStatus } from '@houzy/schemas/partner-cockpit/offer-requests';
import { EngagementContactRequestType } from '@houzy/schemas/partner-cockpit/partner-matching';
import type { TFunction } from 'i18next';

import { OfferRequestTimingGroup, timingGroupForConstraint } from './timing';
import {
    OfferRequestContactOptions,
    OfferRequestCredits,
    OfferRequestLanguage,
    OfferRequestProjectSize,
    OfferRequestStatus,
    type OfferRequestContactOption,
} from './types';

function languageLabelKey(language: OfferRequestLanguage): string {
    switch (language) {
        case OfferRequestLanguage.De:
            return 'OFFER_REQUESTS.FILTERS.LANGUAGE.DE';
        case OfferRequestLanguage.En:
            return 'OFFER_REQUESTS.FILTERS.LANGUAGE.EN';
        case OfferRequestLanguage.Fr:
            return 'OFFER_REQUESTS.FILTERS.LANGUAGE.FR';
        case OfferRequestLanguage.It:
            return 'OFFER_REQUESTS.FILTERS.LANGUAGE.IT';
    }
}

function buildingTypeLabelKey(buildingType: OfferRequestBuildingType): string {
    switch (buildingType) {
        case OfferRequestBuildingType.FAMILY_HOUSE:
            return 'OFFER_REQUESTS.FILTERS.BUILDING_TYPE.FAMILY_HOUSE';
        case OfferRequestBuildingType.APARTMENT:
            return 'OFFER_REQUESTS.FILTERS.BUILDING_TYPE.APARTMENT';
        case OfferRequestBuildingType.BUSINESS:
            return 'OFFER_REQUESTS.FILTERS.BUILDING_TYPE.BUSINESS';
        case OfferRequestBuildingType.OTHER:
            return 'OFFER_REQUESTS.FILTERS.BUILDING_TYPE.OTHER';
    }
}

function statusLabelKey(status: OfferRequestStatus): string {
    switch (status) {
        case OfferRequestStatus.Open:
            return 'OFFER_REQUESTS.STATUS.OPEN';
        case OfferRequestStatus.OfferPending:
            return 'OFFER_REQUESTS.STATUS.OFFER_PENDING';
        case OfferRequestStatus.AppointmentScheduled:
            return 'OFFER_REQUESTS.STATUS.APPOINTMENT_SCHEDULED';
        case OfferRequestStatus.WorkInProgress:
            return 'OFFER_REQUESTS.STATUS.WORK_IN_PROGRESS';
        case OfferRequestStatus.WorkDone:
            return 'OFFER_REQUESTS.STATUS.WORK_DONE';
        case OfferRequestStatus.UserNotAvailable:
            return 'OFFER_REQUESTS.STATUS.USER_NOT_AVAILABLE';
        case OfferRequestStatus.UserLost:
            return 'OFFER_REQUESTS.STATUS.USER_LOST';
        case OfferRequestStatus.UserDeclined:
            return 'OFFER_REQUESTS.STATUS.USER_DECLINED';
        case OfferRequestStatus.TestRequest:
            return 'OFFER_REQUESTS.STATUS.TEST_REQUEST';
        case OfferRequestStatus.PartnerUnreachable:
            return 'OFFER_REQUESTS.STATUS.PARTNER_UNREACHABLE';
        case OfferRequestStatus.RequestRejected:
            return 'OFFER_REQUESTS.STATUS.REQUEST_REJECTED';
    }
}

export const OFFER_REQUEST_LANGUAGE_OPTIONS = Object.values(OfferRequestLanguage).map((value) => ({
    value,
    labelKey: languageLabelKey(value),
}));

export const OFFER_REQUEST_BUILDING_TYPE_OPTIONS = Object.values(OfferRequestBuildingType).map((value) => ({
    value,
    labelKey: buildingTypeLabelKey(value),
}));

function projectSizeLabelKey(size: OfferRequestProjectSize): string {
    switch (size) {
        case OfferRequestProjectSize.Under5k:
            return 'OFFER_REQUESTS.FILTERS.SIZE.UNDER_5K';
        case OfferRequestProjectSize.From5kTo10k:
            return 'OFFER_REQUESTS.FILTERS.SIZE.FROM_5K_TO_10K';
        case OfferRequestProjectSize.From10kTo20k:
            return 'OFFER_REQUESTS.FILTERS.SIZE.FROM_10K_TO_20K';
        case OfferRequestProjectSize.From20kTo50k:
            return 'OFFER_REQUESTS.FILTERS.SIZE.FROM_20K_TO_50K';
        case OfferRequestProjectSize.Over50k:
            return 'OFFER_REQUESTS.FILTERS.SIZE.OVER_50K';
    }
}

export const OFFER_REQUEST_PROJECT_SIZE_OPTIONS = Object.values(OfferRequestProjectSize).map((value) => ({
    value,
    labelKey: projectSizeLabelKey(value),
}));

function timingGroupLabelKey(group: OfferRequestTimingGroup): string {
    switch (group) {
        case OfferRequestTimingGroup.EmergencyNow:
            return 'OFFER_REQUESTS.FILTERS.TIMING.EMERGENCY_NOW';
        case OfferRequestTimingGroup.AsSoonAsPossible:
            return 'OFFER_REQUESTS.FILTERS.TIMING.AS_SOON_AS_POSSIBLE';
        case OfferRequestTimingGroup.Within4Weeks:
            return 'OFFER_REQUESTS.FILTERS.TIMING.WITHIN_4_WEEKS';
        case OfferRequestTimingGroup.Within3To6Months:
            return 'OFFER_REQUESTS.FILTERS.TIMING.WITHIN_3_TO_6_MONTHS';
        case OfferRequestTimingGroup.Within12Months:
            return 'OFFER_REQUESTS.FILTERS.TIMING.WITHIN_12_MONTHS';
        case OfferRequestTimingGroup.NotYetDefined:
            return 'OFFER_REQUESTS.FILTERS.TIMING.NOT_YET_DEFINED';
    }
}

function offerRequestTimingGroupLabel(t: TFunction, group: OfferRequestTimingGroup): string {
    return t(timingGroupLabelKey(group));
}

export const OFFER_REQUEST_TIMING_GROUP_OPTIONS = Object.values(OfferRequestTimingGroup).map((value) => ({
    value,
    labelKey: timingGroupLabelKey(value),
}));

export function offerRequestTimingLabel(t: TFunction, timeConstraint: OfferRequestTimeConstraint | undefined): string {
    const group =
        (timeConstraint ? timingGroupForConstraint(timeConstraint) : undefined) ??
        OfferRequestTimingGroup.NotYetDefined;
    return offerRequestTimingGroupLabel(t, group);
}

export function offerRequestProjectSizeLabel(t: TFunction, projectSize: OfferRequestProjectSize): string {
    return t(projectSizeLabelKey(projectSize));
}

const workCategoryTranslationKey = (key: string): string => `WORK_CATEGORY__${key}`;

// The manual title from lead qualification wins; until it exists the work categories stand in for it.
export function offerRequestTitle(
    t: TFunction,
    title: LocalizedText | null,
    workCategoryKeys: string[],
    referenceId: string,
    language: string,
): string {
    const qualifiedTitle = pickLocalizedText(title, language);
    if (qualifiedTitle) {
        return qualifiedTitle;
    }
    return workCategoryKeys.length > 0
        ? workCategoryKeys.map((key) => t(workCategoryTranslationKey(key))).join(', ')
        : `#${referenceId}`;
}

export function offerRequestJobCategories(t: TFunction, workCategoryPaths: string[][]): string {
    return workCategoryPaths
        .map((path) => path.map((key) => t(workCategoryTranslationKey(key))).join(' → '))
        .join(', ');
}

export function offerRequestStatusLabel(t: TFunction, status: OfferRequestStatus): string {
    return t(statusLabelKey(status));
}

function leadStatusLabelKey(leadStatus: PartnerCockpitLeadStatus): string | undefined {
    switch (leadStatus) {
        case 'awaitingResponse':
            return 'OFFER_REQUESTS.KANBAN.PENDING';
        case 'expired':
        case 'rejectedByCustomer':
            return 'OFFER_REQUESTS.CARD.STATE.NO_LONGER_AVAILABLE';
        case 'declinedByPartner':
            return 'OFFER_REQUESTS.CARD.STATE.DECLINED';
        case 'active':
        case 'engaged':
            return undefined;
    }
}

export function offerRequestLeadStatusLabel(t: TFunction, leadStatus: PartnerCockpitLeadStatus): string | undefined {
    const key = leadStatusLabelKey(leadStatus);
    return key === undefined ? undefined : t(key);
}

function contactDetailsLockedHintKey(
    leadStatus: PartnerCockpitLeadStatus,
    isContactRestricted: boolean,
): string | undefined {
    switch (leadStatus) {
        case 'active':
            return 'OFFER_REQUESTS.CONTACT.DETAILS_LOCKED.SEND_REQUEST';
        case 'rejectedByCustomer':
            return 'OFFER_REQUESTS.CONTACT.DETAILS_LOCKED.REJECTED';
        case 'awaitingResponse':
        case 'declinedByPartner':
        case 'expired':
        case 'engaged':
            return isContactRestricted ? 'OFFER_REQUESTS.CONTACT.DETAILS_LOCKED.REIMBURSED' : undefined;
    }
}

export function offerRequestContactDetailsLockedHint(
    t: TFunction,
    leadStatus: PartnerCockpitLeadStatus,
    isContactRestricted: boolean,
): string | undefined {
    const key = contactDetailsLockedHintKey(leadStatus, isContactRestricted);
    return key === undefined ? undefined : t(key);
}

export function formatCreditsLabel(t: TFunction, credits: number): string {
    return credits === 1 ? t('OFFER_REQUESTS.CREDITS_ONE') : t('OFFER_REQUESTS.CREDITS_MANY', { count: credits });
}

export const OFFER_REQUEST_STATUS_OPTIONS = Object.values(OfferRequestStatus).map((value) => ({
    value,
    labelKey: statusLabelKey(value),
}));

export const OFFER_REQUEST_SETTABLE_STATUS_OPTIONS = settableOfferRequestReportStatusSchema.options.map((value) => ({
    value,
    labelKey: statusLabelKey(value),
}));

export const OFFER_REQUEST_CREDITS_OPTIONS = OfferRequestCredits.map((value) => ({
    value: String(value),
    credits: value,
}));

// Whether the option asks the partner for a price (drives the request form layout).
export function contactOptionRequiresPrice(option: OfferRequestContactOption): boolean {
    return option === 'OfferNonBindingPrice' || option === 'OfferBindingPrice';
}

function contactOptionLabelKey(option: OfferRequestContactOption): string {
    switch (option) {
        case 'ContactViaPhoneOrEmail':
            return 'OFFER_REQUESTS.CONTACT.OPTION.CONTACT_VIA_PHONE_OR_EMAIL';
        case 'ShowInterest':
            return 'OFFER_REQUESTS.CONTACT.OPTION.SHOW_INTEREST';
        case 'ShowInterestWithMessage':
            return 'OFFER_REQUESTS.CONTACT.OPTION.SHOW_INTEREST_WITH_MESSAGE';
        case 'OfferNonBindingPrice':
            return 'OFFER_REQUESTS.CONTACT.OPTION.OFFER_GUIDE_PRICE';
        case 'OfferBindingPrice':
            return 'OFFER_REQUESTS.CONTACT.OPTION.OFFER_GUARANTEED_PRICE';
    }
}

export function offerRequestContactOptionLabel(t: TFunction, option: OfferRequestContactOption): string {
    return t(contactOptionLabelKey(option));
}

// Listed in the order they appear in the design's radio group, which matches the
// declaration order in the `OfferRequestContactOption` source of truth.
const OFFER_REQUEST_CONTACT_OPTIONS = OfferRequestContactOptions.map((value) => ({
    value,
    labelKey: contactOptionLabelKey(value),
}));

// The server rejects this option for a subscription partner, whose engagement is tracked through the chat,
// so offering it would let them pick an option that can only 409.
export function offerRequestContactOptionsFor({
    isOnOldContract,
}: {
    isOnOldContract: boolean;
}): typeof OFFER_REQUEST_CONTACT_OPTIONS {
    if (isOnOldContract) {
        return OFFER_REQUEST_CONTACT_OPTIONS;
    }
    return OFFER_REQUEST_CONTACT_OPTIONS.filter(
        (option) => option.value !== EngagementContactRequestType.ContactViaPhoneOrEmail,
    );
}
